import { useEffect } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";

import { useAuth } from "@/lib/use-auth";
import { getSessionToken } from "@/lib/session";
import Cookies from "js-cookie";

/** Which top-level area each role is allowed into. */
const HOME_FOR_ROLE: Record<string, string> = {
  admin: "/admin",
  employee: "/employee",
  superadmin: "/superadmin",
  teamleader: "/teamleader",
};

/** Where each role signs in again. */
const LOGIN_FOR_ROLE: Record<string, string> = {
  admin: "/login/admin",
  employee: "/login/employee",
  superadmin: "/login/superadmin",
  teamleader: "/login/teamleader",
};

/** The server ended this session for good: logged out, expired, blocked or deleted. */
const isSessionEnded = (error: unknown) =>
  error instanceof AxiosError && [401, 404, 410].includes(error.response?.status ?? 0);

export const ProtectedRoute = () => {
  const { isError, isPending, error } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();

  const token = getSessionToken();
  const userRole = localStorage.getItem("userRole") as string | null;

  // A team leader whose session the server has ended ("log out all sessions",
  // expiry, or a blocked account): drop the dead credentials and send them to
  // their own login page with an explanation. Every other role keeps the
  // existing behaviour below.
  const teamLeaderSessionEnded = userRole === "teamleader" && isError && isSessionEnded(error);

  useEffect(() => {
    if (!teamLeaderSessionEnded) return;
    Cookies.remove("key");
    localStorage.removeItem("userRole");
  }, [teamLeaderSessionEnded]);

  // No token although a role is still stored: the session ended without a
  // logout (its cookie expired, or was lost with nothing left to restore it
  // from). Say so and go to that role's login page, rather than silently
  // showing the home page.
  const sessionOver = !token && !!userRole;

  useEffect(() => {
    if (!sessionOver) return;
    // The cached identity has to go: the public pages send anyone they still
    // recognise back in here, and the two redirects would chase each other.
    queryClient.clear();
    // Deferred, and deliberately not cancelled on unmount: on a fresh page load
    // this runs before the toast container (rendered after the routes) has
    // mounted, and a toast raised then is lost.
    setTimeout(() => {
      toast.warning("Your session has ended. Please log in again.", {
        id: "session-ended",
        duration: 10_000,
      });
    }, 0);
  }, [sessionOver, queryClient]);

  if (sessionOver) {
    return <Navigate to={LOGIN_FOR_ROLE[userRole as string] ?? "/"} replace />;
  }

  if (!token) {
    return <Navigate to="/" />;
  }

  if (isPending) {
    return <div>Loading...</div>;
  }

  if (teamLeaderSessionEnded) {
    return <Navigate to="/login/teamleader?ended=1" replace />;
  }

  if (isError) {
    return <Navigate to="/" />;
  }

  // Keep each role inside its own area. Without this a signed-in team leader
  // could open /admin and see the admin shell — every API call 403s so no data
  // appears, but the chrome alone is misleading. Only acts when the role is
  // known, so a session with no stored role behaves exactly as before.
  const home = userRole ? HOME_FOR_ROLE[userRole] : undefined;
  if (home && !location.pathname.startsWith(home)) {
    return <Navigate to={home} replace />;
  }

  return <Outlet />;
};
